// Onboarding data layer (Module 02 §5). TanStack Query hooks wrapping the
// generated api-client — doctrine 3: apps/web reaches the API only through
// @assessly/api-client, never a hand-rolled fetch and never the DB.
//
// Conventions carried from the catalogue area (areas/office/catalogue/hooks):
// query keys co-located in `onboardingKeys`; every mutation sends a fresh
// `Idempotency-Key` and invalidates what it affects; errors normalise through
// `unwrap` into `RequestError` (request id + problem code + field errors) or
// `UnauthorizedError` (401 → /login).
//
// Redaction note (§7): import-row money fields and people phone/email are
// nulled SERVER-side for roles without cost/PII visibility, and the row page
// carries `redacted_fields[]` so the UI renders a lock, not a blank. Never
// treat an absent money field as 0.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../lib/api";
// `ReadContext` annotates the three queryFns whose `refetchInterval` callback
// reads `query.state.data` — see the type's doc comment for why inference needs
// the help there and nowhere else.
import { refreshQuery, type ReadContext } from "../../../lib/query";
import { RequestError, newIdempotencyKey, unwrap } from "../../../lib/request";
import { isCode, WIZARD_PROBLEM_CODES } from "../lib/wizard-problems";
import { isWorking } from "../screens/imports/import-model";

type S = components["schemas"];

export type ImportListQuery = NonNullable<paths["/v1/imports"]["get"]["parameters"]["query"]>;
export type ImportRowsQuery = NonNullable<
  paths["/v1/imports/{id}/rows"]["get"]["parameters"]["query"]
>;
export type TerminologyLocale = NonNullable<
  NonNullable<paths["/v1/terminology"]["get"]["parameters"]["query"]>
>["locale"];
export type InviteListQuery = NonNullable<paths["/v1/invites"]["get"]["parameters"]["query"]>;

// ---- query keys -------------------------------------------------------------

export const onboardingKeys = {
  all: ["onboarding"] as const,
  session: () => ["onboarding", "session"] as const,
  checklist: () => ["onboarding", "checklist"] as const,
  verticals: () => ["onboarding", "verticals"] as const,
  vertical: (key: string) => ["onboarding", "vertical", key] as const,
  currentInstall: () => ["onboarding", "install", "current"] as const,
  updatePreview: () => ["onboarding", "install", "update-preview"] as const,
  terminology: (locale: TerminologyLocale | undefined) =>
    ["onboarding", "terminology", locale ?? null] as const,
  imports: (query: ImportListQuery = {}) => ["onboarding", "imports", query] as const,
  import: (id: string) => ["onboarding", "import", id] as const,
  importRows: (id: string, query: ImportRowsQuery = {}) =>
    ["onboarding", "import", id, "rows", query] as const,
  bindings: (facilityId: string) => ["onboarding", "bindings", facilityId] as const,
  invites: (query: InviteListQuery = {}) => ["onboarding", "invites", query] as const,
  roles: () => ["onboarding", "roles"] as const,
} as const;

// Broad prefixes for post-mutation invalidation.
const SESSION = ["onboarding", "session"] as const;
const CHECKLIST = ["onboarding", "checklist"] as const;
const INSTALL = ["onboarding", "install"] as const;
const TERMINOLOGY = ["onboarding", "terminology"] as const;
const IMPORTS = ["onboarding", "imports"] as const;
const INVITES = ["onboarding", "invites"] as const;

function idempotentHeader() {
  return { "Idempotency-Key": newIdempotencyKey() } as const;
}

/**
 * Freshness window for the reads MORE THAN ONE COMPONENT ON A SCREEN OBSERVES.
 *
 * Without it those reads are issued TWICE per screen open, measured
 * (`__tests__/mount-cost.test.tsx`, which pins the fix). react-query's default
 * `staleTime` is 0 and its default `refetchOnMount` is true, so data is stale
 * the instant it arrives and every LATER observer of the same key fires a second
 * identical request the moment it mounts. Three keys hit that here, and the
 * shape is structural rather than accidental:
 *
 *   * the session — `OnboardingLayout` fetches it and HOLDS ITS SKELETON on it,
 *     so the routed step screen (and `StepContainer`, and `WizardHomeScreen`)
 *     can only subscribe after the first fetch has already settled. Guaranteed
 *     second request, on every onboarding screen, including the resume screen
 *     where it was one third of the page's entire API cost;
 *   * invites and routing-step bindings — the plant screen WARMS both so its
 *     follow-up region can reveal atomically (§5 render-once), and the panels
 *     that own them then mount and re-ask. The warming was buying a reveal and
 *     paying a whole extra round trip for it.
 *
 * 30 s is chosen against what it can cost, not by feel. Every mutation in this
 * file INVALIDATES the keys it moves, and `invalidateQueries` refetches active
 * observers whatever their staleTime — so nothing this client does can be
 * missed. A concurrent admin's write is caught by the §9.1 409 STALE_PAYLOAD
 * path, which re-reads the session on the spot. What is left is a passive
 * reader seeing an up-to-30-s-old completeness score, and a full reload (new
 * QueryClient) always reads through.
 */
const SHARED_READ_STALE_MS = 30 * 1000;

/**
 * Everything the wizard commits can move the completeness score, and the score
 * is refreshed by a post-commit trigger on the server (§3.6). So every wizard
 * mutation invalidates BOTH the session (its cached `checklist_summary`) and the
 * checklist — the UI never shows a score the last action already changed.
 */
function invalidateWizard(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: SESSION }),
    qc.invalidateQueries({ queryKey: CHECKLIST }),
  ]);
}

// =============================================================================
// Session + steps (§5 session group)
// =============================================================================

/**
 * `GET /v1/onboarding/session` — the wizard's spine: the session row, all four
 * step states, and the `checklist_summary` (score + blocking_unmet) the shell's
 * dial renders. The endpoint AUTO-CREATES the session on first call, so a
 * brand-new tenant gets a `draft` session rather than a 404.
 *
 * The shell observes this AND so does whatever it routes to, which is why it
 * carries `SHARED_READ_STALE_MS` — read that comment before removing it.
 */
export function useOnboardingSession(enabled = true) {
  return useQuery({
    queryKey: onboardingKeys.session(),
    // `enabled` defaults to true so every wizard screen is unchanged. The
    // office home passes its reader's grant: `GET /v1/onboarding/session`
    // needs onboarding.view, and a floor worker's home asked anyway and got
    // a 403 on every visit.
    enabled,
    retry: false,
    staleTime: SHARED_READ_STALE_MS,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/onboarding/session", { signal }), "onboarding-session"),
  });
}

/** `PATCH /v1/onboarding/session` — set the target go-live date. 409 once live. */
export function usePatchOnboardingSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["OnboardingSessionPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/onboarding/session", {
          params: { header: idempotentHeader() },
          body,
        }),
        "patch-onboarding-session",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION }),
  });
}

/**
 * `PUT /v1/onboarding/steps/{step_no}/payload` — save a step's scratch state.
 * Pass the `updated_at` the screen loaded with to get optimistic-concurrency
 * checking: a concurrent save answers 409 `STALE_PAYLOAD` carrying the current
 * server state (read it with `stalePayloadServerStep`). Omitting `updated_at`
 * is last-write-wins and should be reserved for a first save.
 */
export function useSaveStepPayload(stepNo: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["OnboardingStepPayloadPutRequest"]) =>
      unwrap(
        await client.PUT("/v1/onboarding/steps/{step_no}/payload", {
          params: { path: { step_no: stepNo }, header: idempotentHeader() },
          body,
        }),
        "save-step-payload",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION }),
    // A 409 STALE_PAYLOAD is only recoverable if the NEXT save carries a fresh
    // `updated_at`. The cached session still holds the token the server just
    // rejected, so without this the user re-applies their edits, presses Save,
    // and 409s again — forever (§9.1 / acceptance M02-S10). Re-reading the
    // session is safe: every screen seeds its editor on `step.id`, which a
    // payload save never changes, so nothing the user re-typed is wiped.
    onError: (error: unknown) => {
      if (isCode(error, WIZARD_PROBLEM_CODES.stalePayload)) {
        return qc.invalidateQueries({ queryKey: SESSION });
      }
      return undefined;
    },
  });
}

/**
 * `POST /v1/onboarding/steps/{step_no}/complete` — mark the step done. 422
 * `PRECONDITIONS_UNMET` lists what is missing (read it with
 * `preconditionFailure`); completing the fourth step promotes the session to
 * `ready_for_review` and triggers a checklist re-evaluation server-side.
 */
export function useCompleteStep(stepNo: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/onboarding/steps/{step_no}/complete", {
          params: { path: { step_no: stepNo }, header: idempotentHeader() },
        }),
        "complete-step",
      ),
    onSuccess: () => invalidateWizard(qc),
  });
}

// =============================================================================
// Terminology (§5 terminology group, §2.5)
// =============================================================================

/**
 * `GET /v1/terminology` — the EFFECTIVE label per canonical term key, already
 * resolved server-side tenant → pack → base. `source` says which layer won.
 * Prefer the `useTerm` hook (hooks/terminology.ts) over reading this directly.
 */
export function useTerminology(locale?: TerminologyLocale) {
  return useQuery({
    queryKey: onboardingKeys.terminology(locale),
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/terminology", {
          signal,
          params: { query: locale !== undefined ? { locale } : {} },
        }),
        "terminology",
      ),
  });
}

/** `PUT /v1/terminology/{term_key}` — upsert ONE locale of a tenant override. */
export function useUpsertTerminology() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { termKey: string; body: S["TerminologyUpsertRequest"] }) =>
      unwrap(
        await client.PUT("/v1/terminology/{term_key}", {
          params: { path: { term_key: input.termKey }, header: idempotentHeader() },
          body: input.body,
        }),
        "upsert-terminology",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TERMINOLOGY }),
  });
}

/**
 * `DELETE /v1/terminology/{term_key}?locale=` — reset the override. Per D-035
 * this reverts a tenant-EDITED pack term to BASE, not to the pack value (one row
 * per key/locale); a standalone pack override the tenant never edited resets to
 * the pack label. The response says which layer now answers.
 */
export function useResetTerminology() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { termKey: string; locale: NonNullable<TerminologyLocale> }) =>
      unwrap(
        await client.DELETE("/v1/terminology/{term_key}", {
          params: {
            path: { term_key: input.termKey },
            query: { locale: input.locale },
            header: idempotentHeader(),
          },
        }),
        "reset-terminology",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TERMINOLOGY }),
  });
}

// =============================================================================
// Vertical packs (§5 verticals group, §3.2)
// =============================================================================

/** `GET /v1/verticals` — every bundled pack (no per-tenant plan filter yet). */
export function useVerticals() {
  return useQuery({
    queryKey: onboardingKeys.verticals(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/verticals", { signal }), "verticals"),
  });
}

/** `GET /v1/verticals/{key}` — the whole pack, for the S2 preview drawer. */
export function useVertical(key: string) {
  return useQuery({
    queryKey: onboardingKeys.vertical(key),
    retry: false,
    enabled: key.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/verticals/{key}", { signal, params: { path: { key } } }),
        "vertical",
      ),
  });
}

/**
 * `GET /v1/verticals/installs/current` — the active (or latest) install with its
 * per-kind disposition. A tenant that has never installed gets 404
 * `PACK_INSTALL_NOT_FOUND`, which is a legitimate state, not an error: this hook
 * maps it to `null` so S2 renders its empty state instead of an error card.
 *
 * Pass `pollWhileInstalling` on the S2 progress panel: the install is
 * asynchronous (§3.2), so the screen polls until the status leaves `installing`.
 */
export function useCurrentInstall(options: { pollWhileInstalling?: boolean } = {}) {
  const poll = options.pollWhileInstalling ?? false;
  return useQuery({
    queryKey: onboardingKeys.currentInstall(),
    retry: false,
    refetchInterval: (query) => {
      if (!poll) return false;
      return query.state.data?.install.status === "installing" ? 2000 : false;
    },
    queryFn: async ({ signal }: ReadContext) => {
      const result = await client.GET("/v1/verticals/installs/current", { signal });
      try {
        return unwrap(result, "current-install");
      } catch (error) {
        if (error instanceof RequestError && error.httpStatus === 404) return null;
        throw error;
      }
    },
  });
}

/**
 * `POST /v1/verticals/{key}/install` — 202; the worker seeds asynchronously, so
 * the caller polls `useCurrentInstall({ pollWhileInstalling: true })`.
 * `force: true` switches packs (supersedes the previous install, §3.2 pt 4).
 *
 * The install read is REFRESHED (cancel-then-invalidate, lib/query.ts) rather
 * than invalidated, and here that is load-bearing rather than defensive. S2
 * gates its pack grid on `useVerticals`, NOT on `useCurrentInstall`, so Install
 * is clickable while the current-install read is still on its FIRST fetch. A
 * plain invalidate then coalesces into that pre-install read, whose answer is
 * "no install" (or the superseded one) — and `pollWhileInstalling` only polls
 * while `status === "installing"`, so the progress panel never starts polling
 * and strands on the pre-install state for the whole seed. The user's only
 * recovery is a page reload.
 */
export function useInstallVertical() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; force?: boolean }) =>
      unwrap(
        await client.POST("/v1/verticals/{key}/install", {
          params: { path: { key: input.key }, header: idempotentHeader() },
          body: input.force === undefined ? {} : { force: input.force },
        }),
        "install-vertical",
      ),
    onSuccess: () => Promise.all([refreshQuery(qc, INSTALL), invalidateWizard(qc)]),
  });
}

// =============================================================================
// The previewed pack update (spec 02 §3.2a, D-343)
// =============================================================================

export type PackUpdatePreview = S["PackUpdatePreview"];
export type PackUpdatePreviewEntry = S["PackUpdatePreviewEntry"];

/**
 * `GET /v1/verticals/installs/current/update-preview` — what the deployed pack
 * would add, change and remove for this tenant. group_admin only
 * (`onboarding.install_pack`); the caller passes `enabled: false` to a reader
 * without the code so no request is made that the server will refuse. A tenant
 * with no install answers 404, mapped to `null` like `useCurrentInstall`.
 */
export function usePackUpdatePreview(enabled: boolean) {
  return useQuery({
    queryKey: onboardingKeys.updatePreview(),
    enabled,
    retry: false,
    queryFn: async ({ signal }: ReadContext) => {
      const result = await client.GET("/v1/verticals/installs/current/update-preview", {
        signal,
      });
      try {
        return unwrap(result, "pack-update-preview");
      } catch (error) {
        if (error instanceof RequestError && error.httpStatus === 404) return null;
        throw error;
      }
    },
  });
}

/**
 * `POST /v1/verticals/installs/current/update` — 202 with the new `update`
 * install row; the worker applies it, so the caller polls
 * `useCurrentInstall({ pollWhileInstalling: true })`. Refreshes the install
 * read (the same load-bearing refresh `useInstallVertical` explains). The
 * preview is only marked stale here: read now, it would meet the update row
 * still installing and answer 409 PACK_INSTALL_IN_PROGRESS, which the screen
 * would show as a failed read for the whole apply. The screen reads it again
 * once the polled install has settled.
 */
export function useApplyPackUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      availableDigest: string;
      accept: { kind: string; key: string }[];
    }) =>
      unwrap(
        await client.POST("/v1/verticals/installs/current/update", {
          params: { header: idempotentHeader() },
          body: { available_digest: input.availableDigest, accept: input.accept },
        }),
        "apply-pack-update",
      ),
    onSuccess: () =>
      Promise.all([
        refreshQuery(qc, onboardingKeys.currentInstall()),
        qc.invalidateQueries({ queryKey: onboardingKeys.updatePreview(), refetchType: "none" }),
        invalidateWizard(qc),
      ]),
  });
}

// =============================================================================
// Imports (§5 imports group, §3.3)
// =============================================================================

/**
 * `GET /v1/imports` — cursor page of jobs, newest first.
 *
 * Polls every 5 s while any listed job is still in an async worker stage
 * (`parsing` / `committing`) and stops as soon as they all settle. Without it a
 * user who goes "back to the step" watches a job frozen at Parsing with no way
 * to know it finished except reloading the page.
 */
export function useImports(query: ImportListQuery = {}) {
  return useQuery({
    queryKey: onboardingKeys.imports(query),
    retry: false,
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((job) => isWorking(job.status)) ? 5000 : false,
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(await client.GET("/v1/imports", { signal, params: { query } }), "imports"),
  });
}

/**
 * `GET /v1/imports/{id}` — job detail. `pollWhileWorking` follows the async
 * parse/commit worker stages (§3.3 state machine) until the job settles.
 */
export function useImport(id: string, options: { pollWhileWorking?: boolean } = {}) {
  const poll = options.pollWhileWorking ?? false;
  return useQuery({
    queryKey: onboardingKeys.import(id),
    retry: false,
    enabled: id.length > 0,
    refetchInterval: (query) => {
      if (!poll) return false;
      const status = query.state.data?.status;
      return status === "parsing" || status === "committing" ? 2000 : false;
    },
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(await client.GET("/v1/imports/{id}", { signal, params: { path: { id } } }), "import"),
  });
}

/** `GET /v1/imports/{id}/rows` — the row grid page + its `redacted_fields[]`. */
export function useImportRows(id: string, query: ImportRowsQuery = {}) {
  return useQuery({
    queryKey: onboardingKeys.importRows(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/imports/{id}/rows", { signal, params: { path: { id }, query } }),
        "import-rows",
      ),
  });
}

/** `POST /v1/imports` — creates the job AND returns the presigned upload URL. */
export function useCreateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ImportCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/imports", { params: { header: idempotentHeader() }, body }),
        "create-import",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: IMPORTS }),
  });
}

/**
 * Everything a parse / remap / validate / commit / discard moves.
 *
 * The ROW pages are REFRESHED (cancel-then-invalidate, lib/query.ts) because
 * the row grid re-keys on every filter change (`importRows(id, query)`), so a
 * page the user just filtered to is on its FIRST fetch — and Validate and
 * Commit sit on that same screen, a click away. A plain invalidate would
 * coalesce into the pre-validation read and leave the grid showing flags the
 * server has already cleared, with nothing scheduled to correct it.
 *
 * The job detail and the job list stay plain: the detail screen renders a
 * skeleton until its own read settles, so no action can fire against a first
 * fetch, and the list is a different screen.
 */
function invalidateImport(qc: ReturnType<typeof useQueryClient>, id: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: onboardingKeys.import(id) }),
    refreshQuery(qc, ["onboarding", "import", id, "rows"]),
    qc.invalidateQueries({ queryKey: IMPORTS }),
  ]);
}

/** `POST /v1/imports/{id}/parse` — 202; the parse worker fills the row table. */
export function useParseImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/imports/{id}/parse", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "parse-import",
      ),
    onSuccess: () => invalidateImport(qc, id),
  });
}

/** `PATCH /v1/imports/{id}/mapping` — confirm the column mapping. */
export function useSetImportMapping(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ImportMappingPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/imports/{id}/mapping", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "set-import-mapping",
      ),
    onSuccess: () => invalidateImport(qc, id),
  });
}

/** `POST /v1/imports/{id}/validate` — SYNCHRONOUS; returns the validated job. */
export function useValidateImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/imports/{id}/validate", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "validate-import",
      ),
    onSuccess: () => invalidateImport(qc, id),
  });
}

/**
 * `POST /v1/imports/{id}/commit` — 202; the commit worker writes through the
 * owning modules. 422 `has_errors` unless `allow_partial: true` (error rows are
 * then skipped, §3.3 pt 5).
 */
export function useCommitImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ImportCommitRequest"]) =>
      unwrap(
        await client.POST("/v1/imports/{id}/commit", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "commit-import",
      ),
    onSuccess: () => Promise.all([invalidateImport(qc, id), invalidateWizard(qc)]),
  });
}

/** `POST /v1/imports/{id}/discard` — cancel a job before commit. */
export function useDiscardImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/imports/{id}/discard", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "discard-import",
      ),
    onSuccess: () => invalidateImport(qc, id),
  });
}

// =============================================================================
// Plant map (§5, §3.4)
// =============================================================================

/**
 * `POST /v1/onboarding/plant-map/commit` — diff + commit the draft tree through
 * the owning modules. Serialised per tenant by an advisory lock: 503
 * `RESOURCE_BUSY` means another wizard commit holds it (retry), 409
 * `ENTITY_IN_USE` carries the refused nodes (`refusedNodes`), 422
 * `DUPLICATE_CODE` a duplicate code in the draft. `pending[]` on a 200 is the
 * honest "this entity type's module is not wired yet" report, not a failure.
 */
export function useCommitPlantMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["PlantMapCommitRequest"]) =>
      unwrap(
        await client.POST("/v1/onboarding/plant-map/commit", {
          params: { header: idempotentHeader() },
          body,
        }),
        "commit-plant-map",
      ),
    onSuccess: () => invalidateWizard(qc),
  });
}

// =============================================================================
// S5 side panels — bindings (Module 01 §2.18) and invites (Module 16)
// =============================================================================
//
// Neither surface belongs to Module 02, but §4 S5 puts both on the plant screen
// because both feed go-live checks the wizard is answerable for
// (`catalogue.routings_bound`, the three `people.*` items). They are reached
// through the same generated client as everything else (doctrine 3).

/** `GET /v1/catalogue/routing-step-bindings?facility_id=` — the facility's
 * step-template → work-center bindings, so S5 can badge UNBOUND templates from
 * ground truth rather than from an unsaved draft.
 *
 * WARMED by the plant screen and then observed again by `BindingsPanel` when the
 * follow-up region reveals, hence `SHARED_READ_STALE_MS`: without it the warm-up
 * bought an atomic reveal and paid a duplicate request for it. */
export function useRoutingStepBindings(facilityId: string) {
  return useQuery({
    queryKey: onboardingKeys.bindings(facilityId),
    retry: false,
    enabled: facilityId.length > 0,
    staleTime: SHARED_READ_STALE_MS,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/routing-step-bindings", {
          signal,
          params: { query: { facility_id: facilityId } },
        }),
        "routing-step-bindings",
      ),
  });
}

/** `PUT /v1/catalogue/routing-step-bindings` — REPLACES the facility's whole
 * set, so callers must always send every binding they want to keep. */
export function usePutRoutingStepBindings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["RoutingStepBindingsPutRequest"]) =>
      unwrap(
        await client.PUT("/v1/catalogue/routing-step-bindings", {
          params: { header: idempotentHeader() },
          body,
        }),
        "put-routing-step-bindings",
      ),
    onSuccess: (data, body) => {
      qc.setQueryData(onboardingKeys.bindings(body.facility_id), data);
      return invalidateWizard(qc);
    },
  });
}

/** `GET /v1/invites` — the S5 People panel's invite grid. Warmed by the plant
 * screen and then observed again by `PeoplePanel`, hence `SHARED_READ_STALE_MS`
 * — same duplicate-request shape as the bindings read above. */
export function useInvites(query: InviteListQuery = {}) {
  return useQuery({
    queryKey: onboardingKeys.invites(query),
    retry: false,
    staleTime: SHARED_READ_STALE_MS,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/invites", { signal, params: { query } }), "invites"),
  });
}

/** `GET /v1/roles` — invites are keyed by `role_id`, so the picker needs them. */
export function useRoles() {
  return useQuery({
    queryKey: onboardingKeys.roles(),
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => unwrap(await client.GET("/v1/roles", { signal }), "roles"),
  });
}

/** `POST /v1/invites` — invite one person into a role at a scope. */
export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["InviteCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/invites", { params: { header: idempotentHeader() }, body }),
        "create-invite",
      ),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: INVITES }), invalidateWizard(qc)]),
  });
}

/** `POST /v1/invites/{id}/revoke` — withdraw an invite that has not been used. */
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      unwrap(
        await client.POST("/v1/invites/{id}/revoke", {
          params: { path: { id: input.id }, header: idempotentHeader() },
        }),
        "revoke-invite",
      ),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: INVITES }), invalidateWizard(qc)]),
  });
}

/** `POST /v1/invites/{id}/resend` — capped at 5 resends server-side. */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      unwrap(
        await client.POST("/v1/invites/{id}/resend", {
          params: { path: { id: input.id }, header: idempotentHeader() },
        }),
        "resend-invite",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVITES }),
  });
}

// =============================================================================
// Go-live checklist (§5 checklist group, §3.6)
// =============================================================================

/**
 * `GET /v1/onboarding/checklist` — a PURE read: it never evaluates and never
 * writes. `items: []` with `score: 0` means "never evaluated", which S8 must
 * render as 19 pending placeholders + a "Run evaluation" CTA — never an implied
 * all-clear.
 */
export function useChecklist() {
  return useQuery({
    queryKey: onboardingKeys.checklist(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/onboarding/checklist", { signal }), "onboarding-checklist"),
  });
}

/** `POST /v1/onboarding/checklist/evaluate` — synchronous; 200 with the refreshed
 * checklist. 409 `ALREADY_LIVE` once the tenant is live (the wizard is frozen). */
export function useEvaluateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/onboarding/checklist/evaluate", {
          params: { header: idempotentHeader() },
        }),
        "evaluate-checklist",
      ),
    onSuccess: (data) => {
      qc.setQueryData(onboardingKeys.checklist(), data);
      return qc.invalidateQueries({ queryKey: SESSION });
    },
  });
}

/** `POST /v1/onboarding/checklist/{item_key}/waive` — reason ≥ 10 chars; a
 * BLOCKING item additionally requires group_admin (403 otherwise). */
export function useWaiveChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemKey: string; reason: string }) =>
      unwrap(
        await client.POST("/v1/onboarding/checklist/{item_key}/waive", {
          params: { path: { item_key: input.itemKey }, header: idempotentHeader() },
          body: { reason: input.reason },
        }),
        "waive-checklist-item",
      ),
    onSuccess: (data) => {
      qc.setQueryData(onboardingKeys.checklist(), data);
      return qc.invalidateQueries({ queryKey: SESSION });
    },
  });
}

/** `POST /v1/onboarding/checklist/{item_key}/unwaive` — the item is RE-EVALUATED,
 * never left on the verdict it carried before the waiver. */
export function useUnwaiveChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemKey: string }) =>
      unwrap(
        await client.POST("/v1/onboarding/checklist/{item_key}/unwaive", {
          params: { path: { item_key: input.itemKey }, header: idempotentHeader() },
        }),
        "unwaive-checklist-item",
      ),
    onSuccess: (data) => {
      qc.setQueryData(onboardingKeys.checklist(), data);
      return qc.invalidateQueries({ queryKey: SESSION });
    },
  });
}

/**
 * `POST /v1/onboarding/checklist/{item_key}/manual-set` — attest a manual item.
 * Supersedes any waiver on the row (§3.6). 422 `ITEM_NOT_MANUAL` on an auto
 * item; 422 `CHECK_NOT_AVAILABLE` when attesting `channels.floor_pwa_tested`
 * passed while its Module 04 verifier is unwired.
 */
export function useManualSetChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      itemKey: string;
      status: S["ChecklistManualSetRequest"]["status"];
    }) =>
      unwrap(
        await client.POST("/v1/onboarding/checklist/{item_key}/manual-set", {
          params: { path: { item_key: input.itemKey }, header: idempotentHeader() },
          body: { status: input.status },
        }),
        "manual-set-checklist-item",
      ),
    onSuccess: (data) => {
      qc.setQueryData(onboardingKeys.checklist(), data);
      return qc.invalidateQueries({ queryKey: SESSION });
    },
  });
}

/**
 * `POST /v1/onboarding/go-live` — group_admin only. The gate is re-evaluated
 * inside the wizard advisory lock, so this is the decision that counts; the
 * client-side `goLiveBlockers` mirror only explains a disabled button. Refusals
 * are 422 PRECONDITIONS_UNMET / BLOCKING_ITEMS_UNMET / SCORE_BELOW_THRESHOLD
 * (read with `goLiveFailure`), 409 ALREADY_LIVE, 503 RESOURCE_BUSY.
 */
export function useGoLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/onboarding/go-live", { params: { header: idempotentHeader() } }),
        "go-live",
      ),
    onSuccess: () => invalidateWizard(qc),
  });
}
